/**
 * Карта «Астана» для «Полёта птицы» — открытый мир на острове ~1,8×1,8 км.
 * Ось — бульвар Нуржол: Ак Орда → Байтерек → Хан Шатыр; Есиль огибает центр.
 * За рекой — Пирамида, Хазрет Султан, «Қазақ Елі», Дворец Независимости
 * (Digital Bridge). Юг — EXPO с Нур Алем и арка «Мәңгілік Ел». Север —
 * старый город, Абу-Даби Плаза, «Пекин Палас». LRT с поездами от аэропорта.
 * Всё из примитивов по фото-референсам, мир детерминирован по seed.
 * Время суток: 0 — день, 0,5 — закат, 1 — ночь с огнями.
 *
 * Перенесено из прототипа groove-bird-prototype/astana/map.html.
 */
/* eslint-disable */
import * as THREE from 'three';
import type { BirdFlightWorld } from './ekibastuzWorld.ts';
import {
  distanceCuller, facadeMaterial, instancedBySector, mergeStaticMeshes,
} from './worldOptimize.ts';

export function* buildAstanaWorld(scene: THREE.Scene): Generator<number, BirdFlightWorld, void> {
  // ─── Случайность по seed: одинаковый мир у всех игроков ───────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const rnd = mulberry32(20260929);
  const rand = (a, b) => a + rnd() * (b - a);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

  yield 0.06;
  // ─── Небо: шейдерная сфера, цвета меняются со временем суток ─────────────
  const skyUniforms = {
    top: { value: new THREE.Color() }, mid: { value: new THREE.Color() }, horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3() }, sunColor: { value: new THREE.Color() }, sunStrength: { value: 1 },
  };
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1400, 32, 16), new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false, uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `
      uniform vec3 top, mid, horizon, sunColor, sunDir; uniform float sunStrength; varying vec3 vDir;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.18 ? mix(mid, top, smoothstep(0.18, 0.8, h)) : mix(horizon, mid, smoothstep(-0.02, 0.18, h));
        float d = max(dot(normalize(vDir), normalize(sunDir)), 0.0);
        c += sunColor * (pow(d, 2500.0) * 4.0 + pow(d, 90.0) * 0.35) * sunStrength;
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  scene.add(sky);

  // Звёзды на ночном небе.
  const nightStars = (() => {
    const pts = [];
    for (let i = 0; i < 1400; i++) {
      const u = rnd() * Math.PI * 2, v = Math.acos(rand(0.05, 1));
      pts.push(Math.sin(v) * Math.cos(u) * 1350, Math.cos(v) * 1350, Math.sin(v) * Math.sin(u) * 1350);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true, fog: false }));
    scene.add(p); return p;
  })();

  // Туман ближе, как в Экибастузе: дальше 1150 м не видно, и секторы
  // за туманом отсекаются камерой — меньше работы ТВ.
  const fog = new THREE.Fog(0xffffff, 350, 1150);
  scene.fog = fog;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x3a3048, 1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sun);

  const lambert = (color, extra = {}) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });
  const box = new THREE.BoxGeometry(1, 1, 1);

  // Огни: днём тусклые, ночью горят. Каждый — материал + дневной и ночной цвет.
  const lights = [];
  function lightMat(dayColor, nightColor) {
    const m = new THREE.MeshBasicMaterial({ color: dayColor });
    lights.push({ m, day: new THREE.Color(dayColor), night: new THREE.Color(nightColor) });
    return m;
  }

  // Инстансинг: сотни одинаковых объектов — один draw call.
  // Инстансинг по секторам — куски вне камеры не рисуются (как в Экибастузе).
  // 800 м: у старта 208 вызовов против 261 при 400 и 456 без оптимизаций.
  const SECTOR_SIZE = 800;
  function instanced(geo, material, list) {
    if (list.length) instancedBySector(scene, geo, material, list, SECTOR_SIZE);
  }
  function add(geo, material, x, y, z, sx = 1, sy = 1, sz = 1, ry = 0) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); m.rotation.y = ry; scene.add(m); return m;
  }

  yield 0.12;
  // ─── Астана: остров и зоны ───────────────────────────────────────────────
  // Ось — бульвар Нуржол с востока (Ак Орда) на запад (Хан Шатыр), в центре
  // Байтерек. Есиль огибает центр с востока и севера. За рекой на востоке —
  // Пирамида, Хазрет Султан, «Қазақ Елі», Дворец Независимости (Digital
  // Bridge). Юг — EXPO со сферой Нур Алем и аркой «Мәңгілік Ел». Север —
  // старый город, Абу-Даби Плаза. Масштаб игровой: дом ≈ 2–3 птицы, знаковые
  // объекты нарочно крупные.
  const ISLAND = 900;
  const redLights = [];

  function height(x, z) {
    const r = Math.hypot(x, z);
    // Аэропорт стоит на ровном: холмы вокруг него гасим.
    const hill = smooth(ISLAND * 0.8, ISLAND * 1.15, r) * smooth(160, 280, Math.hypot(x - 540, z - 700));
    return (Math.sin(x * 0.01) * Math.cos(z * 0.012) * 0.5 + 0.5) * 45 * hill;
  }

  // Земля: город — серо-зелёный газон и плитка, за городом — степь.
  {
    const seg = 120;
    // Высоты — по узлам сетки, потом разворот в треугольники: в 6 раз
    // меньше вызовов height(), чем по развёрнутой сетке (как в Алматы).
    const grid = new THREE.PlaneGeometry(ISLAND * 2.6, ISLAND * 2.6, seg, seg);
    grid.rotateX(-Math.PI / 2);
    const gridPos = grid.attributes.position;
    for (let i = 0; i < gridPos.count; i++) gridPos.setY(i, height(gridPos.getX(i), gridPos.getZ(i)));
    const geo = grid.toNonIndexed();
    grid.dispose();
    const pos = geo.attributes.position;
    const city = [0x8fae6a, 0x86a563, 0x97b572, 0x8aa86b].map((c) => new THREE.Color(c));
    const steppe = [0xb3a06a, 0xa99762, 0xbba872, 0x9e9460].map((c) => new THREE.Color(c));
    const colors = [];
    for (let f = 0; f < pos.count; f += 3) {
      const cx = (pos.getX(f) + pos.getX(f + 1) + pos.getX(f + 2)) / 3;
      const cz = (pos.getZ(f) + pos.getZ(f + 1) + pos.getZ(f + 2)) / 3;
      const c = Math.hypot(cx, cz) < ISLAND * 0.78 ? pick(city) : pick(steppe);
      for (let k = 0; k < 3; k++) colors.push(c.r, c.g, c.b);
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    scene.add(new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  }

  yield 0.19;
  // ─── Дороги ──────────────────────────────────────────────────────────────
  const lampPoles = [], lampHeads = [];
  const roadMat = lambert(0x44464b);
  const lineMat = lightMat(0xcfc6b0, 0xf2e6c8);
  function road(x1, z1, x2, z2, w, lamps = 0) {
    const dx = x2 - x1, dz = z2 - z1, len = Math.hypot(dx, dz), ry = Math.atan2(dx, dz);
    add(box, roadMat, (x1 + x2) / 2, 0.35, (z1 + z2) / 2, w, 0.3, len, ry);
    add(box, lineMat, (x1 + x2) / 2, 0.55, (z1 + z2) / 2, 0.3, 0.05, len, ry);
    if (lamps) for (let s = 0; s <= len; s += lamps) {
      const x = x1 + dx * s / len + Math.cos(ry) * (w / 2 + 1), z = z1 + dz * s / len - Math.sin(ry) * (w / 2 + 1);
      lampPoles.push({ x, y: 4, z, sx: 0.25, sy: 8, sz: 0.25 });
      lampHeads.push({ x, y: 8.2, z, sx: 0.9, sy: 0.5, sz: 0.9 });
    }
  }

  yield 0.25;
  // ─── Офисные башни и жилые дома (общий генератор) ────────────────────────
  const winOn = [], winOff = [], trunks = [], crowns = [];
  // Обычные дома — корпуса-инстансы с окнами в шейдере (`facadeMaterial`):
  // окна-кубики давали большую часть треугольников кадра. Дома со своим
  // материалом (достопримечательности) по-прежнему с окнами-кубиками.
  const facadeGlow = { value: 0 };
  const glassMats = [0x5d8fb8, 0x6fa3c7, 0x4f7ea8, 0x88a9c4].map((c) => facadeMaterial(c, facadeGlow));
  const panelMats = [0xe6e2d8, 0xd9dce0, 0xeadfca].map((c) => facadeMaterial(c, facadeGlow));
  const facadeMats = new Set([...glassMats, ...panelMats]);
  const bodies = new Map(), roofMat = lambert(0x7c8288);
  function addBody(mat, it) { if (!bodies.has(mat)) bodies.set(mat, []); bodies.get(mat).push(it); }
  const winLit = lightMat(0x5a6470, 0xffd58a);
  const winDark = lightMat(0x5a6470, 0x28303e);
  function tower(x, z, w, d, h, mat, windows = true) {
    if (facadeMats.has(mat)) {
      addBody(mat, { x, y: h / 2, z, sx: w, sy: h, sz: d });
      addBody(roofMat, { x, y: h + 0.4, z, sx: w + 0.4, sy: 0.8, sz: d + 0.4 });
      // Случайные числа тратим как при окнах-кубиках — раскладка города та же.
      if (windows) {
        for (let y = 3; y < h - 2; y += 3.4) {
          for (let u = -w / 2 + 1.5; u < w / 2 - 1; u += 2.6) { rnd(); rnd(); }
          for (let u = -d / 2 + 1.5; u < d / 2 - 1; u += 2.6) { rnd(); rnd(); }
        }
      }
      return;
    }
    add(box, mat, x, h / 2, z, w, h, d);
    add(box, lambert(0x7c8288), x, h + 0.4, z, w + 0.4, 0.8, d + 0.4);
    if (!windows) return;
    for (let y = 3; y < h - 2; y += 3.4) {
      for (let u = -w / 2 + 1.5; u < w / 2 - 1; u += 2.6) {
        [d / 2 + 0.06, -d / 2 - 0.06].forEach((o) => (rnd() < 0.5 ? winOn : winOff).push({ x: x + u, y, z: z + o, sx: 1.4, sy: 1.6, sz: 0.1 }));
      }
      for (let u = -d / 2 + 1.5; u < d / 2 - 1; u += 2.6) {
        [w / 2 + 0.06, -w / 2 - 0.06].forEach((o) => (rnd() < 0.5 ? winOn : winOff).push({ x: x + o, y, z: z + u, sx: 0.1, sy: 1.6, sz: 1.4 }));
      }
    }
  }
  function tree(x, z) {
    const h = rand(6, 9);
    trunks.push({ x, y: h * 0.35, z, sx: 0.45, sy: h * 0.7, sz: 0.45 });
    crowns.push({ x, y: h * 0.8, z, sx: rand(2.2, 3.2), sy: h * 0.42, sz: rand(2.2, 3.2), ry: rnd() * 3 });
  }

  yield 0.31;
  // ─── Нуржол: бульвар от Ак Орды до Хан Шатыра ────────────────────────────
  const AXIS = { east: 360, west: -380 };
  {
    // Зелёная полоса с дорожками, фонтанами и деревьями.
    add(box, lambert(0x7fa45a), 0, 0.25, 0, AXIS.east - AXIS.west, 0.3, 60);
    add(box, lambert(0xd8d1c0), 0, 0.42, 0, AXIS.east - AXIS.west, 0.1, 14);
    road(AXIS.west, -42, AXIS.east, -42, 14, 22);
    road(AXIS.west, 42, AXIS.east, 42, 14, 22);
    const fountain = lightMat(0x6fb6d8, 0x7fd4ff);
    for (let x = AXIS.west + 60; x < AXIS.east - 40; x += 70) {
      if (Math.abs(x) < 40) continue;
      add(new THREE.CylinderGeometry(7, 7, 0.6, 16), fountain, x, 0.6, 0);
    }
    for (let x = AXIS.west + 10; x < AXIS.east; x += 11) [-22, -16, 16, 22].forEach((z) => tree(x + rand(-2, 2), z));
    // Башни вдоль бульвара: стекло к югу, «Изумрудный квартал» к северу.
    for (let x = AXIS.west + 60; x < AXIS.east - 60; x += 48) {
      if (Math.abs(x) < 70) continue;
      tower(x + rand(-5, 5), 78, rand(20, 28), rand(18, 24), rand(30, 55), pick(glassMats));
      tower(x + rand(-5, 5), -78, rand(20, 26), rand(18, 22), rand(28, 50), pick(glassMats));
    }
    // «Изумрудный квартал» — четыре зелёные башни у Байтерека.
    [[-60, -95, 90], [-25, -110, 110], [25, -110, 100], [60, -95, 85]].forEach(([x, z, h]) => tower(x, z, 18, 18, h, lambert(0x3f9c86)));
  }

  yield 0.38;
  // ─── Байтерек, по фото ────────────────────────────────────────────────
  // Пучок белых колонн: у земли чуть шире, выше почти прямо, у платформы
  // расходится короной; длинные белые «шипы» обнимают золотой гранёный шар
  // и поднимаются выше него. Внутри — бирюзовый стеклянный ствол в белых
  // поясах. Внизу круглый фонтан на ступенчатой площади.
  const BAITEREK = { x: 0, z: 0, h: 100 };
  {
    const { x: bx, z: bz } = BAITEREK;
    const white = lambert(0xf4f5f3);
    const beams = [];
    const Y = new THREE.Vector3(0, 1, 0);
    const beam = (a, b, t) => {
      const mid = a.clone().add(b).multiplyScalar(0.5), dir = b.clone().sub(a);
      beams.push({ x: mid.x + bx, y: mid.y, z: mid.z + bz, sx: t, sy: dir.length(), sz: t, q: new THREE.Quaternion().setFromUnitVectors(Y, dir.normalize()) });
    };
    const ring = (a, r, y) => new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r);
    // Колонны ствола: радиус по высоте.
    const col = (y) => y < 8 ? THREE.MathUtils.lerp(8, 6.4, y / 8) : y < 58 ? THREE.MathUtils.lerp(6.4, 5.6, (y - 8) / 50) : THREE.MathUtils.lerp(5.2, 11, (y - 58) / 14);
    const N = 24, ys = [0, 8, 20, 32, 44, 58, 66, 72];
    for (let i = 0; i < N; i++) {
      const a = i / N * Math.PI * 2;
      for (let k = 0; k < ys.length - 1; k++) beam(ring(a, col(ys[k]), ys[k]), ring(a, col(ys[k + 1]), ys[k + 1]), 1.7);
      // Корона: шип от платформы вверх-наружу, выше шара.
      // Шипы кончаются у середины шара — он лежит в корзине, а не в частоколе.
      const len = i % 2 ? 1 : 0.9;
      beam(ring(a, 11, 72), ring(a, 11 + 8 * len, 72 + 14 * len), 0.8);
      // Косые связи между шипами — сетка короны.
      const a2 = (i + 1) / N * Math.PI * 2;
      beam(ring(a, 13, 75), ring(a2, 17, 82), 0.3);
      beam(ring(a2, 13, 75), ring(a, 17, 82), 0.3);
    }
    // Горизонтальные кольца-пояса на пучке.
    [2, 30, 58].forEach((y) => { for (let i = 0; i < N; i++) beam(ring(i / N * Math.PI * 2, col(y), y), ring((i + 1) / N * Math.PI * 2, col(y), y), 0.4); });
    instanced(box, white, beams);
    // Стеклянный ствол в белых поясах.
    add(new THREE.CylinderGeometry(3.6, 3.6, 70, 20), new THREE.MeshPhongMaterial({ color: 0x5fa8a0, specular: 0xffffff, shininess: 60 }), bx, 35, bz);
    const bands = [];
    for (let y = 4; y < 70; y += 2.2) bands.push({ x: bx, y, z: bz, sx: 3.75, sy: 0.35, sz: 3.75 });
    instanced(new THREE.CylinderGeometry(1, 1, 1, 20), white, bands);
    // Платформа под шаром.
    add(new THREE.CylinderGeometry(12, 10, 2, 32), white, bx, 72, bz);
    // Золотой шар из гранёных панелей + антенна.
    const goldTex = (() => {
      const c = document.createElement('canvas'); c.width = 256; c.height = 128;
      const g = c.getContext('2d');
      for (let i = 0; i < 32; i++) for (let j = 0; j < 16; j++) {
        const l = 45 + ((i * 7 + j * 13) % 5) * 5;
        g.fillStyle = `hsl(45, 70%, ${l}%)`; g.fillRect(i * 8, j * 8, 8, 8);
      }
      g.strokeStyle = 'rgba(90,70,20,.6)'; g.lineWidth = 1;
      for (let i = 0; i <= 256; i += 8) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i, 128); g.stroke(); }
      for (let j = 0; j <= 128; j += 8) { g.beginPath(); g.moveTo(0, j); g.lineTo(256, j); g.stroke(); }
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    add(new THREE.SphereGeometry(11, 32, 20), new THREE.MeshPhongMaterial({ map: goldTex, specular: 0xfff2b0, shininess: 90, emissive: 0x2a1c00 }), bx, 84, bz);
    const glow = add(new THREE.SphereGeometry(11.3, 24, 16), lightMat(0x000000, 0xffc861), bx, 84, bz);
    glow.material.transparent = true; glow.material.opacity = 0.55; glow.material.blending = THREE.AdditiveBlending;
    add(new THREE.CylinderGeometry(0.15, 0.3, 8, 6), lambert(0xd8dce0), bx, 99, bz);
    // Фонтан на ступенчатой площади.
    // Большая светлая площадь со звёздным узором, как на фото.
    const plazaTex = (() => {
      const c = document.createElement('canvas'); c.width = c.height = 512;
      const g = c.getContext('2d'); g.fillStyle = '#e4d9cc'; g.fillRect(0, 0, 512, 512);
      g.strokeStyle = '#c98f7a'; g.lineWidth = 5;
      [[8, 230], [8, 150]].forEach(([n, r]) => {
        g.beginPath();
        for (let k = 0; k <= n * 2; k++) { const a = k / (n * 2) * Math.PI * 2, rr = k % 2 ? r * 0.55 : r; g[k ? 'lineTo' : 'moveTo'](256 + Math.cos(a) * rr, 256 + Math.sin(a) * rr); }
        g.stroke();
      });
      g.beginPath(); g.arc(256, 256, 250, 0, Math.PI * 2); g.stroke();
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    const plaza = add(new THREE.CircleGeometry(40, 64), new THREE.MeshLambertMaterial({ map: plazaTex }), bx, 0.55, bz);
    plaza.rotation.x = -Math.PI / 2;
    add(new THREE.CylinderGeometry(22, 24, 1.2, 40), lambert(0xa9745f), bx, 0.6, bz);
    add(new THREE.CylinderGeometry(16, 17, 1.6, 40), lambert(0xb89f86), bx, 1.6, bz);
    add(new THREE.CylinderGeometry(14, 14, 0.3, 40), lightMat(0x6fb6d8, 0x7fd4ff), bx, 2.5, bz);
  }

  yield 0.44;
  // ─── Ак Орда: белое здание, голубой купол со шпилем ──────────────────────
  {
    const x = AXIS.east + 40, z = 0;
    add(box, lambert(0xf1efe8), x, 12, z, 60, 24, 80);
    add(box, lambert(0xe6e2d8), x + 12, 18, z, 26, 36, 40);
    for (let c = -30; c <= 30; c += 5) add(new THREE.CylinderGeometry(0.9, 0.9, 20, 8), lambert(0xffffff), x - 31, 10, z + c);
    add(new THREE.CylinderGeometry(11, 11, 10, 24), lambert(0xf1efe8), x + 12, 41, z);
    add(new THREE.SphereGeometry(11, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0x3f86c8), x + 12, 46, z);
    add(new THREE.CylinderGeometry(0.3, 1.2, 18, 8), lambert(0xd9a93a), x + 12, 65, z);
    // «Золотые банки», по фото: золотые башни-конусы, сужаются кверху,
    // синяя вертикальная полоса, короткий золотой шпиль.
    [-1, 1].forEach((s) => {
      const gx = AXIS.east - 30, gz = s * 95;
      const gold = new THREE.MeshPhongMaterial({ color: 0xd4a93a, specular: 0xfff0b0, shininess: 70, emissive: 0x2a1c00 });
      add(new THREE.CylinderGeometry(6, 10, 60, 24), gold, gx, 30, gz);
      add(box, lambert(0x2a4f8f), gx - 8.2 * s, 30, gz, 1.2, 58, 3).rotation.z = -s * 0.066;
      add(new THREE.ConeGeometry(6, 9, 24), gold, gx, 64.5, gz);
      add(new THREE.CylinderGeometry(0.2, 0.5, 8, 8), gold, gx, 73, gz);
    });
  }

  yield 0.50;
  // ─── Хан Шатыр: вогнутый шатёр, рёбра, «корона» и шпиль ───────────────
  // По фото: бока вогнутые — шатёр широко расходится к земле; ткань
  // серебристо-белая с частыми вертикальными рёбрами; наверху светлый
  // воротник, тёмная полосатая «корона» и тонкий прямой шпиль; внизу
  // светлое кольцо-основание на зелёном холме с круглыми трубами.
  const KHAN = { x: AXIS.west - 70, z: 0 };
  {
    const { x, z } = KHAN;
    const R = 80, H = 95, TOP = 7, BASE_Y = 7;
    // Профиль шатра: радиус падает быстро у земли и медленно к верху.
    const profile = [];
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      profile.push(new THREE.Vector2(TOP + (R - TOP) * Math.pow(1 - t, 2.1), BASE_Y + t * H));
    }
    const fabric = new THREE.MeshPhongMaterial({ color: 0xe8eef3, specular: 0xffffff, shininess: 60, side: THREE.DoubleSide, flatShading: true });
    add(new THREE.LatheGeometry(profile, 64), fabric, x, 0, z);
    // Рёбра по ткани — меридианы шатра.
    const ribPts = [];
    for (let k = 0; k < 64; k++) {
      const a = k / 64 * Math.PI * 2;
      for (let i = 0; i < profile.length - 1; i++) {
        const p0 = profile[i], p1 = profile[i + 1];
        ribPts.push(x + Math.cos(a) * (p0.x + 0.15), p0.y, z + Math.sin(a) * (p0.x + 0.15),
                    x + Math.cos(a) * (p1.x + 0.15), p1.y, z + Math.sin(a) * (p1.x + 0.15));
      }
    }
    const ribs = new THREE.BufferGeometry(); ribs.setAttribute('position', new THREE.Float32BufferAttribute(ribPts, 3));
    scene.add(new THREE.LineSegments(ribs, new THREE.LineBasicMaterial({ color: 0xa9b6c0 })));
    // Ночью шатёр светится изнутри.
    const inner = add(new THREE.LatheGeometry(profile.map((p) => new THREE.Vector2(p.x - 0.8, p.y)), 48), lightMat(0x000000, 0xffe2b0), x, 0, z);
    inner.material.side = THREE.BackSide;
    // Верх: светлый воротник, тёмная полосатая «корона», шпиль.
    const topY = BASE_Y + H;
    add(new THREE.CylinderGeometry(TOP + 1.5, TOP + 2.5, 3, 32), lambert(0xf2f4f6), x, topY - 1, z);
    for (let i = 0; i < 5; i++) {
      add(new THREE.CylinderGeometry(TOP - i * 0.7, TOP - i * 0.7 + 0.3, 1.4, 32), lambert(i % 2 ? 0xd9dde0 : 0x2f3338), x, topY + 1 + i * 1.4, z);
    }
    add(new THREE.CylinderGeometry(0.35, 0.8, 55, 8), lambert(0xf2f4f6), x, topY + 8 + 27, z);
    const tip = add(new THREE.SphereGeometry(1, 8, 6), lightMat(0x8a2a22, 0xff3b30), x, topY + 63, z); redLights.push(tip);
    // Основание: светлое кольцо на зелёном холме, круглые трубы по кругу.
    add(new THREE.CylinderGeometry(R + 4, R + 10, 7, 64), lambert(0xe9e6df), x, 3.5, z);
    add(new THREE.CylinderGeometry(R + 10, R + 26, 4, 64), lambert(0x6f9c4a), x, 1.5, z);
    const tubes = [], caps = [];
    for (let k = 0; k < 18; k++) {
      const a = k / 18 * Math.PI * 2, rr = R + 6;
      tubes.push({ x: x + Math.cos(a) * rr, y: 8, z: z + Math.sin(a) * rr, sx: 2.2, sy: 3, sz: 2.2 });
      caps.push({ x: x + Math.cos(a) * rr, y: 9.6, z: z + Math.sin(a) * rr, sx: 2, sy: 0.3, sz: 2 });
    }
    instanced(new THREE.CylinderGeometry(1, 1, 1, 16), lambert(0x9aa0a6), tubes);
    instanced(new THREE.CylinderGeometry(1, 1, 1, 16), lambert(0x7fc8c0), caps);
    // Красные клумбы-орнамент на склоне.
    for (let k = 0; k < 12; k++) {
      const a = k / 12 * Math.PI * 2 + 0.1, rr = R + 17;
      const bed = add(box, lambert(k % 2 ? 0xd8423a : 0xf1ece2), x + Math.cos(a) * rr, 3.7, z + Math.sin(a) * rr, 14, 0.3, 4);
      bed.rotation.y = -a + Math.PI / 2;
    }
  }

  yield 0.56;
  // ─── Есиль: река и мосты ─────────────────────────────────────────────────
  const RIVER = [[320, 700], [470, 350], [470, 60], [430, -220], [260, -330], [0, -300], [-300, -340], [-620, -300], [-900, -380]];
  {
    const pts = RIVER.map(([x, z]) => new THREE.Vector3(x, 0.5, z));
    const curve = new THREE.CatmullRomCurve3(pts);
    const width = 48, samples = 160, verts = [], idx = [];
    for (let i = 0; i <= samples; i++) {
      const p = curve.getPoint(i / samples), t = curve.getTangent(i / samples);
      const n = new THREE.Vector3(-t.z, 0, t.x).normalize().multiplyScalar(width / 2);
      verts.push(p.x + n.x, 0.5, p.z + n.z, p.x - n.x, 0.5, p.z - n.z);
      if (i < samples) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3)); g.setIndex(idx); g.computeVertexNormals();
    scene.add(new THREE.Mesh(g, new THREE.MeshLambertMaterial({ color: 0x4d88b0, side: THREE.DoubleSide })));
    // Мосты — поперёк реки, с огнями.
    [0.18, 0.36, 0.55, 0.72].forEach((u) => {
      const p = curve.getPoint(u), t = curve.getTangent(u), ry = Math.atan2(t.x, t.z) + Math.PI / 2;
      add(box, lambert(0xb8bcc0), p.x, 4, p.z, 12, 1.4, width + 20, ry);
      add(box, lightMat(0x9aa0a6, 0xffe6a8), p.x, 5.2, p.z, 12.4, 0.3, width + 20, ry);
    });
    // Набережная — деревья вдоль реки.
    for (let i = 0; i < 120; i++) {
      const u = i / 120, p = curve.getPoint(u), t = curve.getTangent(u);
      const n = new THREE.Vector3(-t.z, 0, t.x).normalize();
      [-1, 1].forEach((s) => tree(p.x + n.x * s * (width / 2 + 8), p.z + n.z * s * (width / 2 + 8)));
    }
  }

  yield 0.62;
  // ─── Правый берег на востоке: Пирамида, Хазрет Султан, «Қазақ Елі» ──────
  {
    // Пирамида (Дворец мира и согласия), по фото: острая, светлый фасад с
    // ромбической сеткой белых диагоналей, верхняя треть — синие стеклянные
    // ромбы, внизу ряд маленьких синих ромбов; стоит на зелёном холме,
    // спереди тёмный вход с надписью.
    const px = 600, pz = 40, half = 46, ph = 80, baseY = 6;
    const facade = (() => {
      const c = document.createElement('canvas'); c.width = 1024; c.height = 1024;
      const g = c.getContext('2d');
      g.fillStyle = '#dfe3e6'; g.fillRect(0, 0, 1024, 1024);
      // Мелкая сетка остекления.
      g.strokeStyle = 'rgba(150,160,170,.55)'; g.lineWidth = 2;
      for (let i = -1024; i < 2048; i += 42) {
        g.beginPath(); g.moveTo(i, 1024); g.lineTo(i + 1024, 0); g.stroke();
        g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 1024, 1024); g.stroke();
      }
      // Верхняя треть — синие ромбы.
      const diamond = (cx, cy, w, h, color) => {
        g.fillStyle = color; g.beginPath();
        g.moveTo(cx, cy - h / 2); g.lineTo(cx + w / 2, cy); g.lineTo(cx, cy + h / 2); g.lineTo(cx - w / 2, cy); g.closePath(); g.fill();
      };
      diamond(512, 170, 170, 300, '#1f3f8f');
      diamond(427, 330, 170, 300, '#2a4fa3'); diamond(597, 330, 170, 300, '#2a4fa3');
      g.fillStyle = '#1f3f8f'; g.fillRect(340, 320, 344, 26);
      // Внизу — маленькие синие ромбы.
      [140, 250, 400, 624, 774, 884].forEach((x) => diamond(x, 930, 34, 60, '#23489a'));
      // Большая ромбическая сетка белых диагоналей.
      g.strokeStyle = '#ffffff'; g.lineWidth = 10;
      for (let k = -4; k <= 4; k++) {
        const x = 512 + k * 256;
        g.beginPath(); g.moveTo(x, 1024); g.lineTo(x + 1024, -1024); g.stroke();
        g.beginPath(); g.moveTo(x, 1024); g.lineTo(x - 1024, -1024); g.stroke();
      }
      g.beginPath(); g.moveTo(170, 700); g.lineTo(854, 700); g.stroke();
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    // Четыре треугольные грани: низ грани — низ текстуры, вершина — середина верха.
    {
      const corners = [[-half, -half], [half, -half], [half, half], [-half, half]];
      const pos = [], uv = [];
      for (let i = 0; i < 4; i++) {
        const [x0, z0] = corners[i], [x1, z1] = corners[(i + 1) % 4];
        pos.push(x0, 0, z0, x1, 0, z1, 0, ph, 0);
        uv.push(0, 0, 1, 0, 0.5, 1);
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      geo.computeVertexNormals();
      const pyr = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ map: facade, side: THREE.DoubleSide }));
      pyr.position.set(px, baseY, pz); scene.add(pyr);
      // Рёбра пирамиды — белые.
      const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ color: 0xffffff }));
      edges.position.copy(pyr.position); scene.add(edges);
      // Ночью светится верхушка.
      const glow = add(new THREE.ConeGeometry(half * 0.34, ph * 0.34, 4), lightMat(0x000000, 0x6f9cff), px, baseY + ph * 0.83, pz);
      glow.rotation.y = Math.PI / 4; glow.material.transparent = true; glow.material.opacity = 0.5; glow.material.blending = THREE.AdditiveBlending;
    }
    // Зелёный холм и тёмный вход с надписью спереди (с запада, со стороны Ак Орды).
    add(box, lambert(0x6f9c4a), px, baseY / 2, pz, half * 2 + 20, baseY, half * 2 + 20);
    add(box, lambert(0x1c2026), px - half - 11, baseY / 2, pz, 6, baseY - 0.5, 34);
    {
      const c = document.createElement('canvas'); c.width = 512; c.height = 48;
      const g = c.getContext('2d'); g.fillStyle = '#0e1116'; g.fillRect(0, 0, 512, 48);
      g.fillStyle = '#ffffff'; g.font = 'bold 24px -apple-system, sans-serif'; g.textAlign = 'center';
      g.fillText('БЕЙБІТШІЛІК ЖӘНЕ КЕЛІСІМ САРАЙЫ', 256, 32);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
      const sign = add(new THREE.PlaneGeometry(30, 2.8), new THREE.MeshBasicMaterial({ map: t }), px - half - 14.1, baseY - 1, pz);
      sign.rotation.y = -Math.PI / 2;
    }
    // Хазрет Султан: белый корпус, бирюзовый купол, четыре минарета.
    const mx = 620, mz = 220;
    add(box, lambert(0xf4f2ec), mx, 10, mz, 70, 20, 70);
    add(new THREE.CylinderGeometry(20, 22, 10, 24), lambert(0xf4f2ec), mx, 25, mz);
    add(new THREE.SphereGeometry(21, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0x3fb3b7), mx, 30, mz);
    add(new THREE.CylinderGeometry(0.4, 1, 10, 8), lambert(0xd9a93a), mx, 56, mz);
    [[-38, -38], [38, -38], [-38, 38], [38, 38]].forEach(([dx, dz]) => {
      add(new THREE.CylinderGeometry(2.2, 2.6, 58, 12), lambert(0xf4f2ec), mx + dx, 29, mz + dz);
      add(new THREE.ConeGeometry(2.6, 8, 12), lambert(0x3fb3b7), mx + dx, 62, mz + dz);
    });
    // «Қазақ Елі»: белая колонна с золотой птицей Самрук.
    const kx = 560, kz = -140;
    add(new THREE.CylinderGeometry(3, 4.5, 80, 16), lambert(0xf2f2ee), kx, 40, kz);
    const samruk = add(new THREE.ConeGeometry(4, 10, 6), new THREE.MeshLambertMaterial({ color: 0xd9a93a, emissive: 0x3a2800 }), kx, 85, kz);
    [-1, 1].forEach((s) => { const w = add(box, samruk.material, kx + s * 6, 86, kz, 10, 0.8, 3); w.rotation.z = s * 0.35; });
    // Дворец Независимости — здесь проходит Digital Bridge.
    const dx = 690, dz = -60;
    add(box, lambert(0xeef2f5), dx, 14, dz, 56, 28, 40);
    add(box, lightMat(0x5d8fb8, 0x9fd0ff), dx - 28.2, 14, dz, 0.4, 24, 36);
    const c = document.createElement('canvas'); c.width = 512; c.height = 128;
    const g = c.getContext('2d'); g.fillStyle = '#0d2b4d'; g.fillRect(0, 0, 512, 128);
    g.fillStyle = '#5fd0ff'; g.font = 'bold 54px -apple-system, sans-serif'; g.textAlign = 'center'; g.fillText('DIGITAL BRIDGE', 256, 70);
    g.fillStyle = '#ffffff'; g.font = '28px -apple-system, sans-serif'; g.fillText('ASTANA 2026', 256, 110);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const banner = add(new THREE.PlaneGeometry(40, 10), new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide }), dx - 30, 36, dz);
    banner.rotation.y = -Math.PI / 2;
  }

  yield 0.69;
  // ─── Юг: EXPO — сфера Нур Алем, павильоны-полумесяцы, арка «Мәңгілік Ел» ─
  // По фото: стеклянный шар с треугольной сеткой и переливами голубого,
  // бирюзового и фиолетового; светлая круглая площадь со звёздным узором;
  // кольцом — изогнутые павильоны с серебристо-голубыми крышами в полосах
  // световых окон и ярусами окон на фасаде; стеклянные переходы между ними;
  // ночью шар горит синим и в небо бьют два луча.
  {
    const ex = 40, ez = 480;
    // Площадь со звёздным узором.
    const plazaTex = (() => {
      const c = document.createElement('canvas'); c.width = c.height = 512;
      const g = c.getContext('2d');
      g.fillStyle = '#d9dde2'; g.fillRect(0, 0, 512, 512);
      g.strokeStyle = '#aeb6c0'; g.lineWidth = 3;
      for (let r = 60; r < 256; r += 40) { g.beginPath(); g.arc(256, 256, r, 0, Math.PI * 2); g.stroke(); }
      g.strokeStyle = '#8fa3b8'; g.lineWidth = 4;
      for (let k = 0; k < 32; k++) { const a = k / 32 * Math.PI * 2; g.beginPath(); g.moveTo(256, 256); g.lineTo(256 + Math.cos(a) * 256, 256 + Math.sin(a) * 256); g.stroke(); }
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    const plaza = add(new THREE.CircleGeometry(78, 64), new THREE.MeshLambertMaterial({ map: plazaTex }), ex, 0.6, ez);
    plaza.rotation.x = -Math.PI / 2;
    const plazaGlow = add(new THREE.RingGeometry(40, 78, 64), lightMat(0x000000, 0x3f7fd0), ex, 0.8, ez);
    plazaGlow.rotation.x = -Math.PI / 2; plazaGlow.material.transparent = true; plazaGlow.material.opacity = 0.6; plazaGlow.material.blending = THREE.AdditiveBlending;

    // Нур Алем: переливающаяся стеклянная сфера + треугольная сетка + свечение.
    const R = 34, cy = R + 3;
    const shell = new THREE.IcosahedronGeometry(R, 3).toNonIndexed();
    const tints = [0x6fa8d8, 0x5f96cc, 0x7fc0e0, 0x5fb0c0, 0x8a86d0, 0x6d7fc8, 0x9ac8e8].map((c) => new THREE.Color(c));
    const cols = [];
    for (let f = 0; f < shell.attributes.position.count / 3; f++) {
      const y = shell.attributes.position.getY(f * 3) / R;
      const c = tints[(f * 7 + Math.floor((y + 1) * 3)) % tints.length];
      for (let k = 0; k < 3; k++) cols.push(c.r, c.g, c.b);
    }
    shell.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
    shell.computeVertexNormals();
    add(shell, new THREE.MeshPhongMaterial({ vertexColors: true, flatShading: true, specular: 0xffffff, shininess: 90, transparent: true, opacity: 0.92 }), ex, cy, ez);
    const grid = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(R + 0.2, 3)), new THREE.LineBasicMaterial({ color: 0xe8f4fb }));
    grid.position.set(ex, cy, ez); scene.add(grid);
    const glow = add(new THREE.SphereGeometry(R + 0.6, 32, 20), lightMat(0x000000, 0x2f7fe0), ex, cy, ez);
    glow.material.transparent = true; glow.material.opacity = 0.55; glow.material.blending = THREE.AdditiveBlending;
    // Лучи в небо — только ночью (днём чёрное + аддитивное смешивание = невидимо).
    [-4, 4].forEach((dx) => {
      const beam = add(new THREE.CylinderGeometry(2.2, 0.8, 520, 12, 1, true), lightMat(0x000000, 0x4f9fff), ex + dx, cy + R + 260, ez);
      beam.rotation.z = dx * 0.012;
      beam.material.transparent = true; beam.material.opacity = 0.35; beam.material.blending = THREE.AdditiveBlending; beam.material.depthWrite = false;
      beam.material.side = THREE.DoubleSide;
    });
    // Низкие купола у шара.
    [[-58, 20], [58, 20], [0, -62]].forEach(([dx, dz]) => {
      add(new THREE.CylinderGeometry(13, 13, 5, 32), lambert(0xe3e8ec), ex + dx, 2.5, ez + dz);
      add(new THREE.SphereGeometry(13, 32, 10, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0xa8bccc), ex + dx, 5, ez + dz).scale.y = 0.45;
    });

    // Павильоны-полумесяцы: сектор кольца, вытянутый вверх.
    const roofMat = new THREE.MeshPhongMaterial({ color: 0xb9c9d8, specular: 0xffffff, shininess: 50, side: THREE.DoubleSide });
    const wallMat = lambert(0xdfe3e6);
    const windowTex = (() => {
      const c = document.createElement('canvas'); c.width = 256; c.height = 64;
      const g = c.getContext('2d'); g.fillStyle = '#ffffff'; g.fillRect(0, 0, 256, 64);
      g.fillStyle = '#2b3440'; for (let row = 0; row < 4; row++) g.fillRect(0, 6 + row * 15, 256, 5);
      const t = new THREE.CanvasTexture(c); t.wrapS = THREE.RepeatWrapping; t.repeat.set(12, 1); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    const facade = lightMat(0x9aa7b4, 0xffd9a0); facade.map = windowTex;
    const sector = (r1, r2, a0, a1) => {
      const sh = new THREE.Shape();
      sh.absarc(0, 0, r2, a0, a1, false);
      sh.absarc(0, 0, r1, a1, a0, true);
      return sh;
    };
    const skylights = [];
    const PAV = [[-0.35, 0.95], [1.05, 1.95], [2.3, 3.1], [3.45, 4.35], [4.75, 5.6]];
    PAV.forEach(([a0, a1], i) => {
      const r1 = 96, r2 = 122, h = 18 + (i % 2) * 4;
      // Корпус.
      const body = new THREE.ExtrudeGeometry(sector(r1, r2, a0, a1), { depth: h, bevelEnabled: false, curveSegments: 24 });
      body.rotateX(-Math.PI / 2);
      add(body, wallMat, ex, 0, ez);
      // Фасад с ярусами окон по внешнему и внутреннему краю.
      [r2 + 0.15, r1 - 0.15].forEach((r) => {
        const f = add(new THREE.CylinderGeometry(r, r, h - 3, 48, 1, true, Math.PI / 2 - a1, a1 - a0), facade, ex, (h - 3) / 2 + 1, ez);
        f.material.side = THREE.DoubleSide;
      });
      // Крыша — чуть шире корпуса, со световыми полосами поперёк.
      const roof = new THREE.ExtrudeGeometry(sector(r1 - 3, r2 + 4, a0 - 0.02, a1 + 0.02), { depth: 1.2, bevelEnabled: true, bevelThickness: 0.8, bevelSize: 1.5, bevelSegments: 2, curveSegments: 24 });
      roof.rotateX(-Math.PI / 2);
      add(roof, roofMat, ex, h, ez);
      for (let a = a0 + 0.06; a < a1 - 0.04; a += 0.09) {
        const rm = (r1 + r2) / 2;
        skylights.push({ x: ex + Math.cos(a) * rm, y: h + 2.3, z: ez - Math.sin(a) * rm, sx: 2.2, sy: 0.3, sz: (r2 - r1) * 0.7, ry: a + Math.PI / 2 });
      }
      // Стеклянный арочный переход к следующему павильону.
      const next = PAV[(i + 1) % PAV.length][0], mid = (a1 + (next < a1 ? next + Math.PI * 2 : next)) / 2;
      const rm = (r1 + r2) / 2, gap = ((next < a1 ? next + Math.PI * 2 : next) - a1) * rm;
      const tube = new THREE.Shape(); tube.absarc(0, 0, 6, 0, Math.PI, false); tube.absarc(0, 0, 5.4, Math.PI, 0, true);
      const tg = new THREE.ExtrudeGeometry(tube, { depth: gap + 6, bevelEnabled: false, curveSegments: 12 });
      tg.translate(0, 0, -(gap + 6) / 2);
      const t = add(tg, new THREE.MeshLambertMaterial({ color: 0xbfe0ee, transparent: true, opacity: 0.55, side: THREE.DoubleSide }), ex + Math.cos(mid) * rm, h - 6, ez - Math.sin(mid) * rm);
      t.rotation.y = mid;
    });
    instanced(box, lightMat(0xd8eef8, 0xfff2d0), skylights);

    // Овальная арена — к северо-западу.
    add(new THREE.CylinderGeometry(34, 36, 16, 48), lambert(0xd8dde2), ex - 170, 8, ez - 60).scale.z = 0.7;
    const dome = add(new THREE.SphereGeometry(35, 48, 12, 0, Math.PI * 2, 0, Math.PI / 2), roofMat, ex - 170, 16, ez - 60);
    dome.scale.set(1, 0.3, 0.72);
    // Astana Hub — у EXPO.
    tower(ex - 150, ez + 40, 40, 26, 18, lambert(0xf2f4f6));
    // «Мәңгілік Ел»: белая арка с золотым верхом, на пути от EXPO к центру.
    const ax = 40, az = 290;
    [-1, 1].forEach((s) => add(box, lambert(0xf2f2ee), ax + s * 16, 28, az, 8, 56, 10));
    add(box, new THREE.MeshLambertMaterial({ color: 0xd9a93a, emissive: 0x2a1c00 }), ax, 60, az, 42, 8, 12);
    road(ax, 50, ax, ez - 75, 16, 24);
  }

  yield 0.75;
  // ─── Север за рекой: старый город, Абу-Даби Плаза, телебашня ─────────────
  {
    for (let bx = -700; bx <= 150; bx += 85) {
      for (let bz = -700; bz <= -420; bz += 70) {
        if (Math.hypot(bx, bz) > ISLAND * 0.85) continue;
        const floors = rnd() < 0.6 ? 9 : 5, h = floors * 3;
        if (rnd() < 0.5) { tower(bx, bz - 15, 56, 10, h, pick(panelMats)); tower(bx, bz + 15, 56, 10, h, pick(panelMats)); }
        else { tower(bx - 18, bz, 10, 48, h, pick(panelMats)); tower(bx + 18, bz, 10, 48, h, pick(panelMats)); }
        for (let k = 0; k < 4; k++) tree(bx + rand(-35, 35), bz + rand(-30, 30));
      }
    }
    for (let i = -8; i <= 2; i++) road(i * 85 - 42, -740, i * 85 - 42, -390, 9, 30);
    // Абу-Даби Плаза, по фото: синее зеркальное стекло, две ступени (низ
    // шире), тёмные пояса-полосы, белые вертикальные рёбра по углам,
    // вертикальная надпись QAZAQSTAN, открытая «корона» наверху; рядом две
    // башни пониже в том же стиле.
    {
      const x = -340, z = -190;
      const glassTex = (() => {
        const c = document.createElement('canvas'); c.width = 128; c.height = 512;
        const g = c.getContext('2d');
        const gr = g.createLinearGradient(0, 0, 128, 0);
        gr.addColorStop(0, '#5f86b8'); gr.addColorStop(0.5, '#86aad6'); gr.addColorStop(1, '#4f73a3');
        g.fillStyle = gr; g.fillRect(0, 0, 128, 512);
        g.strokeStyle = 'rgba(40,60,90,.35)'; g.lineWidth = 1;
        for (let y = 0; y < 512; y += 8) { g.beginPath(); g.moveTo(0, y); g.lineTo(128, y); g.stroke(); }
        for (let xx = 0; xx < 128; xx += 32) { g.beginPath(); g.moveTo(xx, 0); g.lineTo(xx, 512); g.stroke(); }
        const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; return t;
      })();
      const glass = (rx, ry) => { const t = glassTex.clone(); t.needsUpdate = true; t.repeat.set(rx, ry); return new THREE.MeshPhongMaterial({ map: t, specular: 0xffffff, shininess: 70 }); };
      const band = lambert(0x3a4148), fin = lambert(0xe9edf0);
      const nightWin = lightMat(0x000000, 0xffd99a);
      const block = (cx, cz, w, h, y0, lit = true) => {
        add(box, glass(w / 12, h / 60), cx, y0 + h / 2, cz, w, h, w);
        if (lit) { const m = add(box, nightWin, cx, y0 + h / 2, cz, w + 0.1, h, w + 0.1); m.material.transparent = true; m.material.opacity = 0.25; m.material.blending = THREE.AdditiveBlending; m.material.depthWrite = false; }
        [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([sx, sz]) => add(box, fin, cx + sx * w / 2, y0 + h / 2, cz + sz * w / 2, 1.2, h, 1.2));
      };
      const bands = (cx, cz, w, ys) => ys.forEach((y) => add(box, band, cx, y, cz, w + 0.3, 3.5, w + 0.3));
      // Главная башня: низ 34 м в ширину до 105 м, верх 30 м до 195 м.
      block(x, z, 32, 105, 0);
      block(x, z, 30, 90, 105);
      bands(x, z, 32, [70]); bands(x, z, 30, [108, 178]);
      // «Корона»: рамка без крыши.
      // «Корона»: открытая рамка из тонких стоек над крышей — сквозь неё видно небо.
      const crown = lambert(0xb9c0c6);
      [[0, 15], [0, -15], [15, 0], [-15, 0]].forEach(([dx, dz]) => add(box, crown, x + dx, 203.5, z + dz, dz ? 30 : 0.8, 1, dz ? 0.8 : 30));
      for (let k = -14; k <= 14; k += 2) {
        [[k, 15], [k, -15], [15, k], [-15, k]].forEach(([dx, dz]) => add(box, crown, x + dx, 199.5, z + dz, 0.5, 8, 0.5));
      }
      add(box, lambert(0x3a4148), x, 195.3, z, 29, 0.6, 29);
      // Надпись QAZAQSTAN сверху вниз на северной грани.
      const c = document.createElement('canvas'); c.width = 96; c.height = 900;
      const g = c.getContext('2d'); g.fillStyle = '#f4f6f8'; g.font = 'bold 84px -apple-system, sans-serif'; g.textAlign = 'center';
      'QAZAQSTAN'.split('').forEach((ch, i) => g.fillText(ch, 48, 90 + i * 96));
      const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
      const txt = add(new THREE.PlaneGeometry(6, 56), new THREE.MeshBasicMaterial({ map: tex, transparent: true }), x + 8, 145, z + 15.2);
      const txt2 = add(new THREE.PlaneGeometry(6, 56), txt.material, x - 8, 145, z - 15.2); txt2.rotation.y = Math.PI;
      const l = add(new THREE.SphereGeometry(1.4, 8, 6), lightMat(0x8a2a22, 0xff3b30), x, 204, z); redLights.push(l);
      // «Пекин Палас» (Бейжің Сарайы), по фото: кремовая башня ~22 этажа с
      // квадратными окнами и стопками балконов по углам; сверху широкая
      // тёмная крыша с загнутыми краями и красной каймой, над ней
      // двухъярусная пагода с красными колоннами и зелёным навершием.
      // Подиум уступами, у каждого уступа своя бирюзовая крыша с поднятыми
      // углами; в центре стеклянный фасад и красная вывеска; красные ворота
      // с синей крышей и флаги у входа.
      {
        const qx = x - 75, qz = z + 60;
        const cream = lambert(0xf6e3b8), stone = lambert(0xe6cfa2), red = lambert(0xb52a22);
        const teal = new THREE.MeshLambertMaterial({ color: 0x1f8fb5, side: THREE.DoubleSide, flatShading: true });
        const dark = new THREE.MeshLambertMaterial({ color: 0x2c2f35, side: THREE.DoubleSide, flatShading: true });
        const trim = lightMat(0xa8322a, 0xff3b2a);
        // Китайская крыша: вогнутые скаты, поднятые углы (лейс с 4 сегментами).
        const roofGeo = (() => {
          const prof = [[0, 0.62], [0.22, 0.58], [0.42, 0.42], [0.62, 0.24], [0.82, 0.1], [0.97, 0.03], [1.08, 0.1]]
            .map(([r, y]) => new THREE.Vector2(r, y)).reverse();
          const g = new THREE.LatheGeometry(prof, 4); g.rotateY(Math.PI / 4); return g;
        })();
        const cnRoof = (cx, cy, cz, w, d, h, mat) => {
          const m = add(roofGeo, mat, cx, cy, cz); m.scale.set(w * 0.707, h / 0.62, d * 0.707);
          // Красная кайма по краю свеса.
          const ex = w * 0.707 * 0.97 * 0.707 * 1.41 / 2, ez = d * 0.707 * 0.97 * 0.707 * 1.41 / 2;
          [[0, ez, w * 0.97, 0.35], [0, -ez, w * 0.97, 0.35], [ex, 0, 0.35, d * 0.97], [-ex, 0, 0.35, d * 0.97]]
            .forEach(([dx, dz, sx, sz]) => add(box, trim, cx + dx, cy + h * 0.06, cz + dz, sx, 0.45, sz));
        };
        const winLitM = winLit, winDarkM = winDark;
        // Окна по граням прямоугольного объёма.
        const windows = (cx, cz, w, d, y0, y1, step = 3.2, gap = 2.6) => {
          for (let y = y0 + 1.6; y < y1 - 1; y += step) {
            for (let u = -w / 2 + 1.6; u < w / 2 - 1; u += gap) [d / 2 + 0.06, -d / 2 - 0.06].forEach((o) => (rnd() < 0.55 ? winOn : winOff).push({ x: cx + u, y, z: cz + o, sx: 1.2, sy: 1.5, sz: 0.1 }));
            for (let u = -d / 2 + 1.6; u < d / 2 - 1; u += gap) [w / 2 + 0.06, -w / 2 - 0.06].forEach((o) => (rnd() < 0.55 ? winOn : winOff).push({ x: cx + o, y, z: cz + u, sx: 0.1, sy: 1.5, sz: 1.2 }));
          }
        };
        void winLitM; void winDarkM;

        // Подиум: центральный корпус и уступы-крылья, у каждого своя крыша.
        const podium = [
          [0, 0, 40, 26, 14],        // центр
          [-26, 4, 16, 22, 11],      // левое крыло
          [26, 4, 16, 22, 11],       // правое крыло
          [-40, 8, 14, 16, 8],       // дальний левый уступ
          [40, 8, 14, 16, 8],
        ];
        podium.forEach(([dx, dz, w, d, h]) => {
          add(box, stone, qx + dx, h / 2, qz + dz, w, h, d);
          windows(qx + dx, qz + dz, w, d, 0, h, 3.6, 3.4);
          cnRoof(qx + dx, h, qz + dz, w + 4, d + 4, 4.5, teal);
        });
        // Второй ярус подиума над центром — ещё одна бирюзовая крыша.
        add(box, cream, qx, 17, qz - 2, 30, 6, 20); cnRoof(qx, 20, qz - 2, 33, 23, 3, teal);
        // Стеклянный фасад по центру и красная вывеска.
        add(box, lightMat(0x2f4a6a, 0x9fd0ff), qx, 8, qz + 13.1, 14, 13, 0.3);
        const c = document.createElement('canvas'); c.width = 512; c.height = 96;
        const g = c.getContext('2d'); g.fillStyle = '#d42a1f'; g.font = 'bold 60px serif'; g.textAlign = 'center';
        g.fillText('Бейжің Сарайы', 256, 68);
        const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
        add(new THREE.PlaneGeometry(14, 2.6), new THREE.MeshBasicMaterial({ map: t, transparent: true, side: THREE.DoubleSide }), qx, 21, qz + 8.2);

        // Башня.
        const H = 76, W = 22, D = 18, tz = qz - 6, y0 = 20;
        add(box, cream, qx, y0 + H / 2, tz, W, H, D);
        windows(qx, tz, W, D, y0, y0 + H);
        // Балконы стопками по углам фасада.
        const balc = [];
        for (let y = y0 + 3; y < y0 + H - 2; y += 3.2) {
          [[-1, 1], [1, 1], [-1, -1], [1, -1]].forEach(([sx, sz]) => {
            balc.push({ x: qx + sx * (W / 2 + 0.6), y, z: tz + sz * (D / 2 - 2.2), sx: 1.2, sy: 0.4, sz: 3.2 });
          });
        }
        instanced(box, lambert(0xf6efe0), balc);
        // Верх: широкая тёмная крыша, пагода в два яруса, навершие.
        const top = y0 + H;
        cnRoof(qx, top, tz, W + 7, D + 7, 5, dark);
        add(box, red, qx, top + 5.5, tz, 13, 4, 11);
        const cols = [];
        for (let k = -2; k <= 2; k++) [[k * 3, 5.7], [k * 3, -5.7]].forEach(([dx, dz]) => cols.push({ x: qx + dx, y: top + 5.5, z: tz + dz, sx: 0.6, sy: 4, sz: 0.6 }));
        instanced(box, lambert(0xd8352a), cols);
        add(box, lightMat(0x5a3a1a, 0xffc060), qx, top + 5.5, tz, 12, 2.6, 10.2);
        cnRoof(qx, top + 7.5, tz, 17, 15, 3.5, dark);
        add(box, red, qx, top + 12, tz, 8, 3, 7);
        cnRoof(qx, top + 13.5, tz, 11, 10, 3, dark);
        add(new THREE.SphereGeometry(0.9, 12, 8), lambert(0x2f8f4a), qx, top + 17.4, tz);

        // Красные ворота с синей крышей и флаги у входа.
        const gx = qx + 22, gz = qz + 24;
        [-6, -2, 2, 6].forEach((dx) => add(new THREE.CylinderGeometry(0.45, 0.45, 7, 10), red, gx + dx, 3.5, gz));
        add(box, lambert(0x2a4f9a), gx, 7.4, gz, 15, 1.2, 2);
        cnRoof(gx, 8, gz, 17, 4, 1.8, teal);
        ['#d42a1f', '#1f5fb5', '#f2f2f2', '#e8c22f'].forEach((col, k) => {
          add(new THREE.CylinderGeometry(0.12, 0.12, 9, 6), lambert(0xdddddd), qx + 4 + k * 3, 4.5, qz + 18);
          add(new THREE.PlaneGeometry(2, 1.3), new THREE.MeshLambertMaterial({ color: col, side: THREE.DoubleSide }), qx + 5 + k * 3, 8.3, qz + 18);
        });
      }
      // Башни-спутники.
      block(x + 48, z + 18, 22, 80, 0); bands(x + 48, z + 18, 22, [48]);
      block(x + 40, z - 36, 18, 64, 0); bands(x + 40, z - 36, 18, [40]);
    }
    // Transport Tower («Зажигалка») — золотая, со скошенным верхом.
    tower(200, -170, 26, 26, 130, new THREE.MeshLambertMaterial({ color: 0xc9a24a, emissive: 0x201400 }));
    const cap = add(box, lambert(0xd4b060), 200, 136, -170, 26, 12, 26); cap.rotation.z = 0.35;
  }

  yield 0.81;
  // ─── LRT: эстакада, станции, поезда, аэропорт ────────────────────────────
  // По фото: бетонная эстакада на Т-образных опорах с перилами; станции —
  // белые арки-«волны» и зелёное стекло; составы бело-серебристые с жёлтой
  // полосой. Маршрут: аэропорт (юго-восток) → EXPO → Нуржол у Хан Шатыра →
  // через Есиль → старый город.
  const LRT_Y = 13;
  const AIRPORT = { x: 540, z: 700 };
  const lrtCurve = new THREE.CatmullRomCurve3([
    [470, 660], [380, 640], [200, 640], [-60, 630], [-200, 520], [-205, 300], [-205, 70],
    [-210, -120], [-230, -330], [-260, -520], [-120, -640], [120, -640],
  ].map(([x, z]) => new THREE.Vector3(x, LRT_Y, z)));
  const lrtTrains = [];
  {
    const concrete = lambert(0xc9cbc8), rail = lambert(0xf2f4f6);
    const Y = new THREE.Vector3(0, 1, 0), Z = new THREE.Vector3(0, 0, 1);
    const len = lrtCurve.getLength(), deck = [], rails = [], piers = [], caps = [];
    const step = 6;
    for (let d = 0; d < len - step; d += step) {
      const a = lrtCurve.getPointAt(d / len), b = lrtCurve.getPointAt((d + step) / len);
      const mid = a.clone().add(b).multiplyScalar(0.5), dir = b.clone().sub(a).normalize();
      const q = new THREE.Quaternion().setFromUnitVectors(Z, dir);
      deck.push({ x: mid.x, y: LRT_Y, z: mid.z, sx: 7, sy: 1.8, sz: step + 0.3, q });
      const side = new THREE.Vector3(dir.z, 0, -dir.x);
      [-1, 1].forEach((s) => rails.push({ x: mid.x + side.x * s * 3.3, y: LRT_Y + 1.6, z: mid.z + side.z * s * 3.3, sx: 0.25, sy: 1.3, sz: step + 0.3, q }));
      if (Math.round(d / step) % 6 === 0) {
        piers.push({ x: mid.x, y: LRT_Y / 2 - 1, z: mid.z, sx: 2.2, sy: LRT_Y - 2, sz: 2.2 });
        caps.push({ x: mid.x, y: LRT_Y - 1.6, z: mid.z, sx: 8, sy: 1.4, sz: 2.6, q });
      }
    }
    instanced(box, concrete, deck);
    instanced(box, rail, rails);
    instanced(new THREE.CylinderGeometry(1, 1, 1, 10), concrete, piers);
    instanced(box, concrete, caps);

    // Станции: белые арки-«волны» над путём, зелёное стекло, платформы.
    const white = lambert(0xf4f5f6), glass = new THREE.MeshLambertMaterial({ color: 0x4f9a8a, transparent: true, opacity: 0.75 });
    const glassLit = lightMat(0x4a7f74, 0x9ff0d8);
    [0.24, 0.47, 0.62, 0.9].forEach((u) => {
      const p = lrtCurve.getPointAt(u), t = lrtCurve.getTangentAt(u), ry = Math.atan2(t.x, t.z);
      const g = new THREE.Group(); g.position.set(p.x, 0, p.z); g.rotation.y = ry; scene.add(g);
      const put = (geo, mat, x, y, z, sx = 1, sy = 1, sz = 1) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.scale.set(sx, sy, sz); g.add(m); return m; };
      put(box, white, 0, LRT_Y + 0.4, 0, 16, 0.8, 60);                      // платформа
      put(box, glass, -15, LRT_Y / 2 + 2, 0, 10, LRT_Y + 4, 50);             // стеклянный корпус
      put(box, glassLit, -20.1, LRT_Y / 2 + 2, 0, 0.3, LRT_Y + 2, 46);
      for (let k = -2; k <= 2; k++) {                                         // арки
        const arch = put(new THREE.TorusGeometry(12, 1.1, 8, 24, Math.PI), white, 0, LRT_Y, k * 12);
        arch.scale.set(1, 0.9, 1);
      }
      // Крыша — полукруглая арка, вытянутая вдоль пути.
      const arc = new THREE.Shape();
      arc.absarc(0, 0, 12.6, 0, Math.PI, false);
      arc.absarc(0, 0, 11.9, Math.PI, 0, true);
      const roofGeo = new THREE.ExtrudeGeometry(arc, { depth: 58, bevelEnabled: false, curveSegments: 24 });
      roofGeo.translate(0, 0, -29);
      const roof = put(roofGeo, new THREE.MeshLambertMaterial({ color: 0xe4edf2, side: THREE.DoubleSide, transparent: true, opacity: 0.6 }), 0, LRT_Y, 0);
      roof.scale.set(1, 0.9, 1);
    });

    // Поезда: 4 вагона, бело-серебристые, жёлтая полоса, тёмные окна.
    const body = lambert(0xe9ecef), stripe = lambert(0xf2c230), win = lightMat(0x2d3640, 0xffe7a8), nose = lambert(0x1f2830);
    for (let n = 0; n < 2; n++) {
      const cars = [];
      for (let c = 0; c < 4; c++) {
        const car = new THREE.Group();
        const add2 = (mat, y, sy, sx = 3.2, sz = 12) => { const m = new THREE.Mesh(box, mat); m.scale.set(sx, sy, sz); m.position.y = y; car.add(m); };
        add2(body, 2.2, 3.2);
        add2(stripe, 1.2, 0.5, 3.25);
        add2(win, 2.7, 1.1, 3.25, 11);
        if (c === 0) { const m = new THREE.Mesh(box, nose); m.scale.set(3.1, 1.6, 0.4); m.position.set(0, 2.6, 6.1); car.add(m); }
        scene.add(car); cars.push(car);
      }
      lrtTrains.push({ cars, u: n * 0.5, dir: n ? -1 : 1 });
    }

    // Аэропорт Нурсултан Назарбаев, по фото: старый терминал — длинный
    // корпус с бело-зелёным остеклением и бирюзово-белым куполом, вывески;
    // новый терминал — пять «волн» серебристой крыши над стеклом; телетрапы,
    // перрон, самолёты.
    const ax = AIRPORT.x, az = AIRPORT.z;
    const apron = add(new THREE.PlaneGeometry(320, 170), lambert(0xb9bcbf), ax, 0.7, az + 60); apron.rotation.x = -Math.PI / 2;
    const ox = ax - 70;
    add(box, lambert(0xf0f1ee), ox, 5, az, 110, 10, 36);
    add(box, lightMat(0x2f9a6a, 0x9ff0c0), ox, 5.5, az + 18.1, 106, 6, 0.3);
    add(box, lambert(0xe8ebe6), ox, 11, az, 40, 4, 30);
    const domeTex = (() => {
      const c = document.createElement('canvas'); c.width = 256; c.height = 128;
      const g = c.getContext('2d'); g.fillStyle = '#3fa7a2'; g.fillRect(0, 0, 256, 128);
      g.fillStyle = '#f2f5f4'; [[0, 18], [58, 12], [92, 8]].forEach(([y, h]) => g.fillRect(0, y, 256, h));
      g.fillStyle = '#1f5f66'; for (let x = 0; x < 256; x += 6) g.fillRect(x, 106, 3, 10);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
    })();
    add(new THREE.SphereGeometry(17, 40, 20, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshPhongMaterial({ map: domeTex, shininess: 60, specular: 0xffffff }), ox, 13, az);
    add(new THREE.CylinderGeometry(17.2, 17.2, 2.5, 40), lambert(0xf2f5f4), ox, 13.4, az);
    const sign = (text, sub, x0) => {
      const c = document.createElement('canvas'); c.width = 512; c.height = 96;
      const g = c.getContext('2d'); g.fillStyle = '#d4a93a'; g.font = 'bold 44px -apple-system, sans-serif'; g.textAlign = 'center';
      // Шрифт подгоняется под ширину — иначе края надписи обрезались.
      const fit = (str, size, y) => {
        let px = size; g.font = `bold ${px}px -apple-system, sans-serif`;
        while (g.measureText(str).width > 492 && px > 10) { px -= 2; g.font = `bold ${px}px -apple-system, sans-serif`; }
        g.fillText(str, 256, y);
      };
      fit(text, 44, 44); fit(sub, 36, 88);
      const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
      add(new THREE.PlaneGeometry(30, 5.6), new THREE.MeshBasicMaterial({ map: t, transparent: true, side: THREE.DoubleSide }), x0, 14, az + 18.4);
    };
    sign('НҰРСҰЛТАН НАЗАРБАЕВ', 'ӘУЕЖАЙЫ', ox - 38);
    sign('NURSULTAN NAZARBAYEV', 'Airport', ox + 38);
    const nx = ax + 60;
    add(box, new THREE.MeshLambertMaterial({ color: 0x9fc7d6, transparent: true, opacity: 0.85 }), nx, 7, az, 110, 14, 40);
    add(box, lightMat(0x6f9fb0, 0xbfe8ff), nx, 7, az + 20.1, 106, 12, 0.3);
    const wave = new THREE.Shape();
    wave.moveTo(-12, 0); wave.quadraticCurveTo(-6, 9, 0, 7); wave.quadraticCurveTo(6, 5, 12, 10);
    wave.lineTo(12, 11); wave.quadraticCurveTo(6, 6, 0, 8); wave.quadraticCurveTo(-6, 10, -12, 1); wave.closePath();
    const waveGeo = new THREE.ExtrudeGeometry(wave, { depth: 52, bevelEnabled: false, curveSegments: 10 });
    waveGeo.translate(0, 0, -26);
    const waveMat = new THREE.MeshPhongMaterial({ color: 0xc9ced3, specular: 0xffffff, shininess: 40, side: THREE.DoubleSide });
    for (let k = 0; k < 5; k++) add(waveGeo, waveMat, nx - 44 + k * 22, 13, az);
    const plane = (x, z, ry, tail) => {
      const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = ry; scene.add(g);
      const put = (geo, m, px, py, pz, sx = 1, sy = 1, sz = 1) => { const o = new THREE.Mesh(geo, m); o.position.set(px, py, pz); o.scale.set(sx, sy, sz); g.add(o); return o; };
      const white = lambert(0xf4f5f6);
      put(new THREE.CapsuleGeometry(2, 30, 6, 12), white, 0, 4, 0).rotation.x = Math.PI / 2;
      put(box, white, 0, 3.6, 2, 34, 0.5, 6);
      put(box, white, 0, 4.5, 15, 12, 0.4, 3);
      put(box, lambert(tail), 0, 8.5, 15, 0.5, 8, 5);
      [-7, 7].forEach((ex) => { put(new THREE.CylinderGeometry(1, 1, 4, 12), lambert(0xd0d3d6), ex, 2.4, 0).rotation.x = Math.PI / 2; });
    };
    [[-95, 0.2, 0xc8323a], [-50, -0.1, 0x2d5fa8], [40, 0, 0x2d5fa8], [90, 0.15, 0xc8323a]].forEach(([dx, ry, tail]) => {
      plane(ax + dx, az + 62, ry + Math.PI, tail);
      add(box, lambert(0xdfe2e4), ax + dx, 5, az + 32, 3, 3, 24);
    });
  }
  function updateLRT(dt) {
    const len = lrtCurve.getLength(), speed = 22 / len;
    lrtTrains.forEach((tr) => {
      tr.u += tr.dir * speed * dt;
      if (tr.u > 1 || tr.u < 0) { tr.dir *= -1; tr.u = THREE.MathUtils.clamp(tr.u, 0, 1); }
      tr.cars.forEach((car, i) => {
        const u = THREE.MathUtils.clamp(tr.u - tr.dir * i * (12.6 / len), 0, 1);
        const p = lrtCurve.getPointAt(u), t = lrtCurve.getTangentAt(u).multiplyScalar(tr.dir);
        car.position.set(p.x, LRT_Y + 0.9, p.z);
        car.rotation.y = Math.atan2(t.x, t.z);
      });
    });
  }
  updateLRT(0);

  // Кольцевая дорога и подъезды — связывают зоны.
  road(-ISLAND * 0.72, 0, AXIS.west - 80, 0, 16, 40);
  road(AXIS.east + 80, 0, 520, 0, 16, 40);
  road(0, 130, 0, 290, 16, 24);

  bodies.forEach((list, mat) => instanced(box, mat, list));
  instanced(box, winLit, winOn);
  instanced(box, winDark, winOff);
  // Деревья и фонари — только в DETAIL_RANGE от птицы (как в Алматы).
  const DETAIL_SECTOR = 400, DETAIL_RANGE = 800;
  const detail = distanceCuller(DETAIL_RANGE);
  const nearOnly = (geo, material, list) => { if (list.length) detail.add(instancedBySector(scene, geo, material, list, DETAIL_SECTOR)); };
  nearOnly(box, lambert(0xece6da), trunks);
  nearOnly(new THREE.IcosahedronGeometry(1, 0), lambert(0x6f9c4a), crowns);
  nearOnly(box, lambert(0x6a6d73), lampPoles);
  nearOnly(box, lightMat(0x8a8474, 0xffd98a), lampHeads);

  yield 0.88;
  // ─── Звёзды для сбора ────────────────────────────────────────────────────
  const starShape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = i / 10 * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 0.45 : 1;
    i ? starShape.lineTo(Math.cos(a) * r, Math.sin(a) * r) : starShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  const starGeo = new THREE.ExtrudeGeometry(starShape, { depth: 0.35, bevelEnabled: false }); starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00 });
  const pickups = [];
  function star(x, y, z) { const s = add(starGeo, starMat, x, y, z, 2.4, 2.4, 2.4); pickups.push(s); }
  // Звёзды: спираль вокруг Байтерека до шара, цепочка над Нуржолом, сквозь
  // арку «Мәңгілік Ел», кольцо вокруг Нур Алем, вдоль Есиля над мостами,
  // над вершиной Пирамиды и к Абу-Даби Плаза.
  for (let i = 0; i < 14; i++) { const a = i * 0.9; star(Math.cos(a) * 20, 12 + i * 5, Math.sin(a) * 20); }
  for (let x = AXIS.west + 40; x < AXIS.east; x += 45) star(x, 24, 0);
  [40, 52, 64].forEach((y) => star(40, y - 20, 290));
  for (let i = 0; i < 12; i++) { const a = i / 12 * Math.PI * 2; star(40 + Math.cos(a) * 48, 40, 480 + Math.sin(a) * 48); }
  star(600, 100, 40); star(560, 100, -140);
  for (let k = 0; k < 24; k++) { const p = lrtCurve.getPointAt(k / 24); star(p.x, LRT_Y + 12, p.z); }
  for (let i = 0; i < 6; i++) star(-340 + (i % 2 ? 22 : -22), 40 + i * 25, -190 + (i % 2 ? 22 : -22));


  yield 0.94;
  // ─── Время суток ─────────────────────────────────────────────────────────
  // Ключевые точки: день → закат → ночь.
  const K = [
    { t: 0.0, top: 0x3f7fc4, mid: 0x9cc7e8, horizon: 0xf1ddb8, fog: 0xd6ccb8, sun: 0xfff2dc, sunI: 1.7, hemi: 1.5, sunAlt: 0.6 },
    { t: 0.5, top: 0x2e3a6e, mid: 0xb0708c, horizon: 0xffb266, fog: 0xd9a07e, sun: 0xffa05a, sunI: 1.5, hemi: 1.1, sunAlt: 0.04 },
    { t: 1.0, top: 0x05081a, mid: 0x0f1838, horizon: 0x2a2748, fog: 0x161b33, sun: 0x8fa6ff, sunI: 0.55, hemi: 0.8, sunAlt: -0.25 },
  ];
  const tmpA = new THREE.Color(), tmpB = new THREE.Color();
  function lerpHex(a, b, k) { return tmpA.setHex(a).lerp(tmpB.setHex(b), k).clone(); }
  function setTime(t) {
    const i = t < 0.5 ? 0 : 1, a = K[i], b = K[i + 1], k = (t - a.t) / (b.t - a.t);
    skyUniforms.top.value.copy(lerpHex(a.top, b.top, k));
    skyUniforms.mid.value.copy(lerpHex(a.mid, b.mid, k));
    skyUniforms.horizon.value.copy(lerpHex(a.horizon, b.horizon, k));
    fog.color.copy(lerpHex(a.fog, b.fog, k));
    const sunColor = lerpHex(a.sun, b.sun, k);
    const alt = THREE.MathUtils.lerp(a.sunAlt, b.sunAlt, k);
    const sunDir = new THREE.Vector3(0.45, Math.max(alt, -0.2), -1).normalize();
    sun.position.copy(sunDir).multiplyScalar(1000);
    sun.color.copy(sunColor); sun.intensity = THREE.MathUtils.lerp(a.sunI, b.sunI, k);
    hemi.intensity = THREE.MathUtils.lerp(a.hemi, b.hemi, k);
    hemi.color.copy(lerpHex(0xffffff, 0x9fb0ff, smooth(0.5, 1, t)));
    skyUniforms.sunDir.value.copy(sunDir);
    skyUniforms.sunColor.value.copy(sunColor);
    skyUniforms.sunStrength.value = 1 - smooth(0.62, 0.8, t);
    // Огни зажигаются с закатом.
    const glow = smooth(0.35, 0.75, t);
    lights.forEach((l) => l.m.color.copy(l.day).lerp(l.night, glow));
    facadeGlow.value = glow;
    nightStars.material.opacity = smooth(0.65, 0.95, t);
    fog.near = THREE.MathUtils.lerp(350, 217, smooth(0.5, 1, t));
  }



  yield 0.97;
  // Склейка статики: сотни мешей одного вида в секторе → один draw call.
  // Анимированное (поезда, кабинки), звёзды и огни — как есть.
  mergeStaticMeshes(scene, [sky, nightStars, ...pickups, ...redLights, ...lrtTrains.flatMap((tr) => tr.cars)], lights.map((l) => l.m), SECTOR_SIZE);

  setTime(0);
  return {
    height,
    pickups,
    // Старт на Нуржоле у Ак Орды, курс на Байтерек.
    spawn: { x: 200, y: 40, z: 0, yaw: Math.PI / 2 },
    radius: ISLAND * 0.92,
    maxY: 180,
    update(dt, elapsed, timeOfDay, cameraPosition) {
      setTime(timeOfDay);
      pickups.forEach((s) => { s.rotation.y = elapsed * 2; });
      updateLRT(dt);
      detail.update(cameraPosition);
      redLights.forEach((l) => { l.visible = timeOfDay < 0.35 || Math.sin(elapsed * 3) > -0.3; });
      sky.position.copy(cameraPosition);
      nightStars.position.copy(cameraPosition);
    },
  };
}
